import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CatBadge } from "blocks-nextjs/blocks-react/components/Badge";
import { format } from "date-fns";
import React from "react";
import chargingIcon from "../../assets/Charging.gif";
import errorChargingOption from "../../assets/ErrorChargingOption.png";
import { getBatteryIcon } from "../../lib/getBatteryIcon";
import { getBatteryIconByMake, getBatteryMakeDisplayName, getCESTLocalTime } from "../../pages/Projects/ProjectDetails.helper";
import { getTimezoneAbbr } from "../../shared/helper";
import { formatTimestampWithTimezone } from "../../shared/timestampHelpers";
const NOON_HOUR = 12;
const MINUTES_PER_HOUR = 60;
const ASSET_NAME_DISPLAY_LENGTH = 3;
const DEFAULT_TIMEZONE = "Europe/Amsterdam";
const TIME_PANEL_COMPLETION = "Completion";
const TIME_PANEL_ARRIVAL = "Arrival";
const TIME_PANEL_DEPARTURE = "Departure";
const BADGE_CONTAINER_BOTH_CLASSES = "flex text-sm flex-col gap-1 items-end h-[52px] justify-between w-[13%]";
const BADGE_CONTAINER_SINGLE_CLASSES = "flex text-sm flex-col gap-1 items-end h-[52px] justify-center w-[13%]";
const BADGE_CONTAINER_EMPTY_CLASSES = "flex text-sm flex-col items-end h-[52px] justify-between w-[13%]";
const COLUMN_LABEL_CLASSES = "text-gray-600 text-xs";
const COLUMN_VALUE_CLASSES = "font-semibold text-base";
const COLUMN_18_CLASSES = "flex-column w-[18%] content-center";
const COLUMN_15_CLASSES = "flex-column w-[15%] content-center";
const COLUMN_22_CLASSES = "flex-column w-[22%] content-center";
const COLUMN_28_CLASSES = "flex-column w-[28%] content-center";
const convertMinutesToHours = (minutes) => {
    if (minutes > MINUTES_PER_HOUR) {
        const hours = Math.floor(minutes / MINUTES_PER_HOUR);
        const newMinutes = minutes % MINUTES_PER_HOUR;
        return `${hours} hr ${newMinutes} min`;
    }
    return `${minutes}min`;
};
const formatSourceName = (sourceName) => {
    if (sourceName.includes("_")) {
        return sourceName
            .split("_")
            .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
            .join(" ");
    }
    return sourceName;
};
const getHourMinuteForTimezone = (tz) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        hourCycle: "h23",
        minute: "2-digit",
        timeZone: tz
    })
        .formatToParts(new Date())
        .reduce((acc, p) => {
        if (p.type === "hour")
            acc.hour = Number(p.value);
        if (p.type === "minute")
            acc.minute = Number(p.value);
        return acc;
    }, { hour: 0, minute: 0 });
    return parts;
};
const FutureChargingOptionDetails = (props) => {
    const { assetDetail, chargeFilter, chargingOptionIndex, chargingOptionsData, onChargeOptionClick, selectedChargeOption = "", selectedChargeRow, time, timePanel, timeUnits, timezone } = props;
    const getLocaleTime = () => {
        if (!time || !timeUnits)
            return "";
        let hours = 0;
        let minutes = 0;
        if (typeof time === "string" && time.includes(":")) {
            const [h, m] = time.split(":");
            hours = Number(h);
            minutes = Number(m);
        }
        else {
            hours = Number(time);
            minutes = 0;
        }
        if (timeUnits === "PM" && hours < NOON_HOUR) {
            hours += NOON_HOUR;
        }
        if (timeUnits === "AM" && hours === NOON_HOUR) {
            hours = 0;
        }
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);
        const safeTz = timezone || DEFAULT_TIMEZONE;
        return `${format(date, "p")} ${getTimezoneAbbr(safeTz)}`; // Append timezone abbreviation
    };
    const subtractMinutesFromTime = (minutesToSubtract) => {
        if (!time || !timeUnits)
            return "";
        let hours = 0;
        let minutes = 0;
        if (typeof time === "string" && time.includes(":")) {
            const [h, m] = time.split(":");
            hours = Number(h);
            minutes = Number(m);
        }
        else {
            hours = Number(time);
            minutes = 0;
        }
        if (timeUnits === "PM" && hours < NOON_HOUR) {
            hours += NOON_HOUR;
        }
        if (timeUnits === "AM" && hours === NOON_HOUR) {
            hours = 0;
        }
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes - minutesToSubtract);
        const safeTz = timezone || DEFAULT_TIMEZONE;
        return `${format(date, "p")} ${getTimezoneAbbr(safeTz)}`; // Append timezone abbreviation
    };
    const addMinutesToTime = (extraMinutes) => {
        if (!time || !timeUnits)
            return "";
        let hours = 0;
        let minutes = 0;
        if (typeof time === "string" && time.includes(":")) {
            const [h, m] = time.split(":");
            hours = Number(h);
            minutes = Number(m);
        }
        else {
            hours = Number(time);
            minutes = 0;
        }
        if (timeUnits === "PM" && hours < NOON_HOUR) {
            hours += NOON_HOUR;
        }
        if (timeUnits === "AM" && hours === NOON_HOUR) {
            hours = 0;
        }
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes + extraMinutes);
        const safeTz = timezone || DEFAULT_TIMEZONE;
        return `${format(date, "p")} ${getTimezoneAbbr(safeTz)}`; // Append timezone abbreviation
    };
    const getEarliestCompletionTime = (isFastest) => {
        const completionTimes = chargingOptionsData?.map((data) => {
            return data.chargeOptions.map((chargeOption) => {
                if (Number(chargeOption?.directChargeToMachine?.machineTargetSoc) === Number(chargeFilter) ||
                    isFastest) {
                    if (chargeOption?.directChargeToMachine?.completionTime &&
                        chargeOption?.throughChargeStation?.[0]?.completionTime) {
                        return Math.min(Math.round(Number(chargeOption.directChargeToMachine.completionTime)), Math.round(Number(chargeOption?.throughChargeStation?.[0]?.completionTime)));
                    }
                    else if (chargeOption?.directChargeToMachine?.completionTime) {
                        return Math.round(Number(chargeOption.directChargeToMachine.completionTime));
                    }
                    else {
                        return Math.round(Number(chargeOption?.throughChargeStation?.[0]?.completionTime));
                    }
                }
                else if (Number(chargeOption?.throughChargeStation?.[0].machineTargetSoC) === Number(chargeFilter)) {
                    return Math.round(Number(chargeOption?.throughChargeStation?.[0]?.completionTime));
                }
            });
        });
        const filteredCompletionTimes = completionTimes.map((times) => {
            return times.filter((time) => time !== undefined);
        });
        const allCompletionTimes = filteredCompletionTimes.flat();
        const earliestCompletionTime = Math.min(...allCompletionTimes);
        return earliestCompletionTime;
    };
    const earliestCompletionTime = getEarliestCompletionTime(true);
    const maxChargeCompletionTime = getEarliestCompletionTime(false);
    const renderEarliestCompletionTimeBadges = (completionTime, machineTargetSoC) => {
        if (completionTime === earliestCompletionTime &&
            completionTime === maxChargeCompletionTime &&
            machineTargetSoC === Number(chargeFilter)) {
            return (_jsxs("div", { className: BADGE_CONTAINER_BOTH_CLASSES, "data-test": "fastest-max-charge", children: [_jsx(CatBadge, { text: "Fastest", variant: "green" }), _jsx(CatBadge, { text: "Max Charge", variant: "grey" })] }));
        }
        else if (completionTime === earliestCompletionTime) {
            return (_jsx("div", { className: BADGE_CONTAINER_SINGLE_CLASSES, "data-test": "fastest-charge", children: _jsx(CatBadge, { text: "Fastest", variant: "green" }) }));
        }
        else if (completionTime === maxChargeCompletionTime) {
            return (_jsx("div", { className: BADGE_CONTAINER_SINGLE_CLASSES, "data-test": "max-charge", children: _jsx(CatBadge, { text: "Max Charge", variant: "grey" }) }));
        }
        return _jsx("div", { className: BADGE_CONTAINER_EMPTY_CLASSES, "data-test": "max-charge" });
    };
    const renderThroughChargeStationDetails = (throughChargeStation = []) => {
        return throughChargeStation.map((chargeStation, index) => {
            const { additionalTravelTime = "", completionTime = "", machineTargetSoc = "", sourceName = "", totalTravelTime = "" } = chargeStation;
            // Check if totalTravelTime validation applies
            let showTravelTimeError = false;
            if ((timePanel === TIME_PANEL_ARRIVAL || timePanel === TIME_PANEL_COMPLETION) &&
                targetedCompletionTime !== null &&
                currentCESTMinutes !== null) {
                const diffMinutes = targetedCompletionTime - currentCESTMinutes;
                const totalTravelTimeValue = Math.round(Number(totalTravelTime || 0));
                if (!Number.isNaN(totalTravelTimeValue) && diffMinutes >= 0) {
                    if (diffMinutes < totalTravelTimeValue) {
                        showTravelTimeError = true;
                    }
                    else {
                        if (timePanel === TIME_PANEL_COMPLETION) {
                            // Calculate arrival time in minutes: targetedCompletionTime - machineChargeTime
                            const machineChargeTimeValue = Math.round(Number(chargeStation.machineChargeTime));
                            const resultedArrivalTimeMinutes = targetedCompletionTime - machineChargeTimeValue;
                            if (resultedArrivalTimeMinutes <= currentCESTMinutes) {
                                showTravelTimeError = true;
                            }
                            else {
                                showTravelTimeError = false;
                            }
                        }
                        else if (timePanel === TIME_PANEL_ARRIVAL) {
                            // For Arrival, the arrival time is the targetedCompletionTime itself
                            if (targetedCompletionTime <= currentCESTMinutes) {
                                showTravelTimeError = true;
                            }
                            else {
                                showTravelTimeError = false;
                            }
                        }
                    }
                }
            }
            return (_jsxs(_Fragment, { children: [index > 0 && _jsx("div", { className: "mt-[16px] mx-3 border-b-2 border-b-gray-200" }), _jsxs("div", { className: `flex mt-[16px] mx-3 battery-charging mb-[12px] ${showTravelTimeError ? "justify-center" : "justify-between"}`, children: [_jsxs("div", { className: COLUMN_18_CLASSES, children: [_jsxs("div", { className: COLUMN_VALUE_CLASSES, children: ["+", convertMinutesToHours(Math.round(Number(additionalTravelTime)))] }), _jsx("div", { className: COLUMN_LABEL_CLASSES, children: formatSourceName(sourceName) })] }), showTravelTimeError ? (_jsxs("div", { className: "flex items-center justify-center w-[85%]", children: [_jsx("img", { alt: "Completion time insufficient", className: "max-h-[70px] object-contain", "data-test": "completion-time-error-image", src: errorChargingOption }), _jsxs("p", { className: "text-bold text-gray-600 text-sm text-center", children: ["Machine cannot be charged in given ", timePanel?.toLowerCase(), " time"] })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: COLUMN_15_CLASSES, children: [_jsxs("div", { className: "flex font-semibold text-base", children: [Math.round(Number(machineTargetSoc)), " %", _jsx("span", { className: "ml-[5px] h-[16px]", children: getBatteryIcon(Math.round(Number(machineTargetSoc))) })] }), _jsx("div", { className: COLUMN_LABEL_CLASSES, children: "Machine SoC" })] }), _jsxs("div", { className: COLUMN_22_CLASSES, children: [_jsx("div", { className: COLUMN_VALUE_CLASSES, children: timePanel === TIME_PANEL_COMPLETION
                                                    ? subtractMinutesFromTime(Math.round(Number(chargeStation.machineChargeTime)))
                                                    : timePanel === TIME_PANEL_ARRIVAL
                                                        ? getLocaleTime()
                                                        : timePanel === TIME_PANEL_DEPARTURE
                                                            ? addMinutesToTime(Math.round(Number(chargeStation.totalTravelTime)))
                                                            : null }), _jsx("div", { className: COLUMN_LABEL_CLASSES, children: "Arrival time" })] }), _jsxs("div", { className: COLUMN_28_CLASSES, children: [_jsx("div", { className: COLUMN_VALUE_CLASSES, children: timePanel === TIME_PANEL_COMPLETION
                                                    ? getLocaleTime()
                                                    : timePanel === TIME_PANEL_ARRIVAL
                                                        ? addMinutesToTime(Math.round(Number(chargeStation.machineChargeTime)))
                                                        : timePanel === TIME_PANEL_DEPARTURE
                                                            ? addMinutesToTime(Math.round(Number(chargeStation.completionTime)))
                                                            : null }), _jsx("div", { className: COLUMN_LABEL_CLASSES, children: "Charge completion time" })] }), renderEarliestCompletionTimeBadges(Math.round(Number(completionTime)), Math.round(Number(machineTargetSoc)))] }))] }, `through-charge-station-${index}`)] }));
        });
    };
    const targetedCompletionTime = (() => {
        if ((timePanel !== TIME_PANEL_COMPLETION && timePanel !== TIME_PANEL_ARRIVAL) || !time || !timeUnits)
            return null;
        let h = 0;
        let m = 0;
        if (typeof time === "string" && time.includes(":")) {
            const [hs, ms] = time.split(":");
            h = Number(hs);
            m = Number(ms);
        }
        else if (typeof time === "number") {
            h = Number(time);
            m = 0;
        }
        else {
            return null;
        }
        if (Number.isNaN(h) || Number.isNaN(m))
            return null;
        if (timeUnits === "PM" && h < NOON_HOUR)
            h += NOON_HOUR;
        if (timeUnits === "AM" && h === NOON_HOUR)
            h = 0;
        return h * MINUTES_PER_HOUR + m;
    })();
    const currentCESTMinutes = (() => {
        if ((timePanel !== TIME_PANEL_COMPLETION && timePanel !== TIME_PANEL_ARRIVAL) ||
            targetedCompletionTime === null)
            return null;
        const tz = timezone || DEFAULT_TIMEZONE;
        const { hour, minute } = getHourMinuteForTimezone(tz);
        return hour * MINUTES_PER_HOUR + minute;
    })();
    return (_jsx(_Fragment, { children: chargingOptionsData?.map((data) => {
            const currentRowSelected = selectedChargeRow === chargingOptionIndex;
            let updatedChargeOptions = data.chargeOptions;
            if (currentRowSelected) {
                const selectedCardIndex = data?.chargeOptions?.findIndex((chargeOption) => chargeOption?.chargeId === selectedChargeOption);
                updatedChargeOptions = data?.chargeOptions?.slice(selectedCardIndex);
            }
            return (_jsx("div", { className: "flex text-[16px] py-3", "data-test": `charging-options-${chargingOptionIndex + 1}`, children: updatedChargeOptions.map((chargeOption) => {
                    const make = getBatteryMakeDisplayName(chargeOption.essMetaData.make);
                    const isChargeOptionSelected = selectedChargeOption === chargeOption?.chargeId;
                    // Use shared helper to format timestamps (handles both ISO and time-only formats)
                    const lastUpdatedSoCTimestamp = chargeOption?.essMetaData.lastUpdatedSoCTimestamp;
                    const { formattedTime: formattedTimestamp, timezoneAbbr: timezoneAbbrFromHelper } = formatTimestampWithTimezone(lastUpdatedSoCTimestamp, timezone, "default", (ts) => getCESTLocalTime(ts) || "");
                    // Fallback to local timezone abbreviation if no project timezone available
                    const safeTz = timezone || DEFAULT_TIMEZONE;
                    const timezoneAbbrText = timezone ? timezoneAbbrFromHelper : getTimezoneAbbr(safeTz);
                    // Compute completion time feasibility error (Completion panel only)
                    let completionTimeError = false;
                    if (timePanel === TIME_PANEL_COMPLETION &&
                        targetedCompletionTime !== null &&
                        currentCESTMinutes !== null) {
                        const diffMinutes = targetedCompletionTime - currentCESTMinutes; // minutes available from now until desired completion
                        const machineChargeTime = Number(chargeOption?.directChargeToMachine?.machineChargeTime);
                        if (!Number.isNaN(machineChargeTime) &&
                            diffMinutes >= 0 &&
                            diffMinutes < Math.ceil(machineChargeTime)) {
                            completionTimeError = true;
                        }
                    }
                    // If error, hide all data except header section and show error image
                    if (completionTimeError) {
                        return (_jsxs("div", { className: `w-[584px] min-h-[252px] shrink-0 rounded-xl mx-2 pt-4 border-[2px] border-[#e9e5e5] border-solid bg-white`, "data-test": "charge-option-error-card", children: [_jsxs("div", { className: "flex justify-between mx-3 align-center", children: [_jsxs("div", { className: "flex battery-details", children: [_jsx("img", { alt: "Assets", className: "w-10 h-12 mr-4 flex align-center", "data-test": `Battery-Icon`, src: getBatteryIconByMake(chargeOption.essMetaData.make) }), _jsxs("div", { className: "flex flex-col", children: [_jsx("div", { children: _jsxs("div", { className: "font-bold", children: [chargeOption.essMetaData.assetName.slice(0, ASSET_NAME_DISPLAY_LENGTH), " ", "| ", chargeOption.essMetaData.batteryCapacity, " kWh"] }) }), _jsx("div", { className: "text-xs font-semibold", children: chargeOption.essMetaData.serialNumber
                                                                ? `${chargeOption.essMetaData.serialNumber} - ${make}`
                                                                : make }), _jsxs("div", { className: COLUMN_LABEL_CLASSES, "data-test": "last-updated-time", children: ["Last updated: ", formattedTimestamp, " ", timezoneAbbrText] })] })] }), _jsxs("div", { className: "flex flex-col justify-center soc", children: [_jsxs("div", { className: "flex flex-row font-bold items-center", children: [_jsxs("span", { className: "mr-[7px] text-xl", children: [Math.round(chargeOption.essMetaData.soc), "%"] }), _jsx("span", { children: chargeOption.essMetaData.reportingStatus === "Charging" ? (_jsx("img", { alt: "charging.gif", className: "w-[24px] h-[14px]", src: chargingIcon })) : (getBatteryIcon(Number(chargeOption.essMetaData.soc))) })] }), _jsx("div", { className: "text-gray-500 text-xs", children: "Current SoC" })] })] }), _jsx("div", { className: "mt-[16px] mx-3 border-b-2 border-b-gray-200" }), _jsxs("div", { className: "flex flex-col items-center justify-center py-6", children: [_jsx("img", { alt: "Completion time insufficient", className: "max-h-[140px] object-contain", "data-test": "completion-time-error-image", src: errorChargingOption }), _jsx("p", { className: "text-bold text-gray-600 text-sm mt-4 text-center", children: "Machine cannot be charged in given completion time" })] })] }, chargeOption.essMetaData.assetId));
                    }
                    return (_jsxs("div", { className: `w-[584px] min-h-[252px] shrink-0 rounded-xl mx-2 pt-4 border-[2px] border-[#e9e5e5] border-solid cursor-pointer ${isChargeOptionSelected ? "bg-[#E1E1E1]" : "bg-white"}`, onClick: () => onChargeOptionClick(assetDetail, chargeOption, chargingOptionIndex), children: [_jsxs("div", { className: "flex justify-between mx-3 align-center", children: [_jsxs("div", { className: "flex battery-details", children: [_jsx("img", { alt: "Assets", className: "w-10 h-12 mr-4 flex align-center", "data-test": `Battery-Icon`, src: getBatteryIconByMake(chargeOption.essMetaData.make) }), _jsxs("div", { className: "flex flex-col", children: [_jsx("div", { children: _jsxs("div", { className: "font-bold", children: [chargeOption.essMetaData.assetName.slice(0, ASSET_NAME_DISPLAY_LENGTH), " ", "| ", chargeOption.essMetaData.batteryCapacity, " kWh"] }) }), _jsx("div", { className: "text-xs font-semibold", children: chargeOption.essMetaData.serialNumber
                                                            ? `${chargeOption.essMetaData.serialNumber} - ${make}`
                                                            : make }), _jsxs("div", { className: COLUMN_LABEL_CLASSES, "data-test": "last-updated-time", children: ["Last updated: ", formattedTimestamp, " ", timezoneAbbrText] })] })] }), _jsxs("div", { className: "flex flex-col justify-center soc", children: [_jsxs("div", { className: "flex flex-row font-bold items-center", children: [_jsxs("span", { className: "mr-[7px] text-xl", children: [Math.round(chargeOption.essMetaData.soc), "%"] }), _jsx("span", { children: chargeOption.essMetaData.reportingStatus === "Charging" ? (_jsx("img", { alt: "charging.gif", className: "w-[24px] h-[14px]", src: chargingIcon })) : (getBatteryIcon(Number(chargeOption.essMetaData.soc))) })] }), _jsx("div", { className: "text-gray-500 text-xs", children: "Current SoC" })] })] }), _jsx("div", { className: "mt-[16px] mx-3 border-b-2 border-b-gray-200" }), _jsxs("div", { className: "flex mt-[16px] mx-3 justify-between battery-charging", children: [_jsxs("div", { className: COLUMN_18_CLASSES, children: [_jsx("div", { className: COLUMN_VALUE_CLASSES, children: "Direct" }), _jsx("div", { className: COLUMN_LABEL_CLASSES, children: "to machine" })] }), _jsxs("div", { className: COLUMN_15_CLASSES, children: [_jsxs("div", { className: "flex font-semibold text-base", children: [Math.round(Number(chargeOption.directChargeToMachine.machineTargetSoc)), "%", _jsx("span", { className: "ml-[5px] h-[16px]", children: getBatteryIcon(Number(chargeOption.directChargeToMachine.machineTargetSoc)) })] }), _jsx("div", { className: COLUMN_LABEL_CLASSES, children: "Machine SoC" })] }), _jsxs("div", { className: COLUMN_22_CLASSES, children: [_jsx("div", { className: COLUMN_VALUE_CLASSES, children: timePanel === TIME_PANEL_COMPLETION
                                                    ? subtractMinutesFromTime(Math.round(Number(chargeOption.directChargeToMachine.machineChargeTime)))
                                                    : timePanel === TIME_PANEL_ARRIVAL
                                                        ? getLocaleTime()
                                                        : timePanel === TIME_PANEL_DEPARTURE
                                                            ? addMinutesToTime(Math.round(Number(chargeOption.directChargeToMachine.totalTravelTime)))
                                                            : null }), _jsx("div", { className: COLUMN_LABEL_CLASSES, children: "Arrival time" })] }), _jsxs("div", { className: COLUMN_28_CLASSES, children: [_jsx("div", { className: COLUMN_VALUE_CLASSES, children: timePanel === TIME_PANEL_COMPLETION
                                                    ? getLocaleTime()
                                                    : timePanel === TIME_PANEL_ARRIVAL
                                                        ? addMinutesToTime(Math.round(Number(chargeOption.directChargeToMachine.machineChargeTime)))
                                                        : timePanel === TIME_PANEL_DEPARTURE
                                                            ? addMinutesToTime(Math.round(Number(chargeOption.directChargeToMachine.completionTime)))
                                                            : null }), _jsx("div", { className: COLUMN_LABEL_CLASSES, children: "Charge completion time" })] }), renderEarliestCompletionTimeBadges(Math.round(Number(chargeOption?.directChargeToMachine?.completionTime)), Math.round(Number(chargeOption?.directChargeToMachine?.machineTargetSoc)))] }), _jsx("div", { className: "mt-[16px] mx-3 border-b-2 border-b-gray-200" }), Number(chargeOption.directChargeToMachine.machineTargetSoc) !==
                                Number(chargeFilter) && (_jsx(_Fragment, { children: renderThroughChargeStationDetails(chargeOption?.throughChargeStation) }))] }, chargeOption.essMetaData.assetId));
                }) }, data.assetId));
        }) }));
};
export default FutureChargingOptionDetails;
